import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FlashList } from '@shopify/flash-list';
import { useTranslation } from 'react-i18next';
import { StyleProp, ViewStyle, View, ViewToken } from 'react-native';
import { useGetMessages } from '_api/useMessages';
import { useOwnProfile } from '_api/useProfile';
import { useFindUser } from '_api/useUsers';
import Images from '_images';
import { useAppNavigation } from '_navigator';
import { isWeb, useCalculateMessageRead, WW } from '_utils';
import { track } from '_utils/Amplitude';
import { useBottomSpacer } from '_utils/hooks';
import useIsHelpfulNeighbor from '_utils/messages/useIsHelpfulNeighbor';
import { flattenIniniteResult } from '_utils/misc';
import { screenMargin, smallestMargin } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { CacheImage, HeaderWithNav, HeimeText } from 'Components';
import ActionBanner from 'Components/ActionBanner';
import RowUsersDisplay from 'Components/RowUsersDisplay';
import TouchableHeime from 'Components/TouchableHeime';
import { Sharing } from 'Screens/Home/icons';
import {
    MessageType,
    HelpfulNeighborTag,
    HelpfulNeighborTags,
    isHelpfulNeighborMessage,
    HelpfulNeighborMessage,
} from 'types/message';
import { ExternalUserProfile, isNonDeletedUser } from 'types/User';
import NewTag from './common/MessageItem/components/common/NewTag';
import useIsNewDisplayState from './common/MessageItem/components/common/useIsNewDisplayState';
import useSharingContextMenuEffect from './common/useSharingContextMenuEffect';
import useReadMessage from './useReadMessage';

const HelpfulNeighbor = (): ReactElement => {
    const { t } = useTranslation();
    const { navigate } = useAppNavigation();
    const { data: ownProfile } = useOwnProfile();
    const isHelpfulNeighbor = useIsHelpfulNeighbor(ownProfile?.id ?? 0);
    const { theme } = useThemeContext();
    const markMessages = useReadMessage();
    const isRead = useCalculateMessageRead();
    const {
        data: messages,
        isLoading: isLoadingMessages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGetMessages([MessageType.Helpers]);
    const [visibleItems, setVisibleItems] = useState<Set<HelpfulNeighborTag>>(new Set());

    const handleBecomeHelpfulNeighbor = () => {
        track('Pressed become helpful neighbor banner in Helpful Neighbor overview');
        navigate('SharingNew', { type: MessageType.Helpers });
    };
    useSharingContextMenuEffect([MessageType.Helpers]);

    useEffect(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const findUser = useFindUser();
    const helpfulUsers = flattenIniniteResult(messages)
        .filter(isHelpfulNeighborMessage)
        .reduce(
            (acc, message) => {
                message.helpful_neighbor_tags.forEach((tag) => {
                    if (!acc[tag]) {
                        acc[tag] = {
                            users: [],
                            messages: [],
                        };
                    }
                    acc[tag].messages.push(message);
                    const user = findUser(message.creator_id);
                    if (user && isNonDeletedUser(user)) {
                        acc[tag].users.push(user);
                    }
                });
                return acc;
            },
            {} as Record<
                HelpfulNeighborTag,
                { users: Array<ExternalUserProfile>; messages: Array<HelpfulNeighborMessage> }
            >,
        );

    const getHandleNavigate = (category: HelpfulNeighborTag) => () => {
        track('Pressed helpful neighbor category in Helpful Neighbor overview', { category });
        navigate('HelpfulNeighborCategorySelected', { category });
    };

    type FlatListItem =
        | {
              type: 'content';
              content: ReactElement | null;
          }
        | {
              type: 'category';
              messages: Array<HelpfulNeighborMessage>;
              users: Array<ExternalUserProfile>;
              category: HelpfulNeighborTag;
          };

    const handleViewableItemsChanged = useCallback(
        (info: { viewableItems: Array<ViewToken<FlatListItem>>; changed: Array<ViewToken<FlatListItem>> }) => {
            const newVisibleItems = new Set<HelpfulNeighborTag>(
                info.viewableItems
                    .map(({ item }) => (item.type === 'category' ? item.category : null))
                    .filter((category): category is HelpfulNeighborTag => category !== null),
            );
            setVisibleItems(newVisibleItems);

            const messageIds = info.viewableItems
                .map(({ item }) => item)
                .filter((msg: FlatListItem) => msg.type === 'category')
                .flatMap((msg) => msg.messages.filter((message) => !isRead(message)))
                .map(({ id }) => id);

            if (messageIds.length) {
                markMessages(messageIds, false);
            }
        },
        // FlatList web crashes because of invariant if we change this!
        // eslint-disable-next-line react-hooks/exhaustive-deps
        isWeb() ? [] : [markMessages, isRead],
    );

    const paddingBottom = useBottomSpacer();

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.mainBackground,
                height: '100%',
            }}
        >
            <HeaderWithNav title={t('sharingSelected:messageType:Helpers')} safeArea />
            <FlashList<FlatListItem>
                onViewableItemsChanged={handleViewableItemsChanged}
                viewabilityConfig={{
                    viewAreaCoveragePercentThreshold: 0.1,
                }}
                contentContainerStyle={{ paddingHorizontal: screenMargin, paddingBottom }}
                data={[
                    {
                        type: 'content',
                        content: isHelpfulNeighbor ? null : (
                            <ActionBanner
                                onPress={handleBecomeHelpfulNeighbor}
                                title={t('sharingAll:becomeHelpfulNeighbor')}
                                message={t('sharingAll:becomeHelpfulNeighborDescription')}
                                icon="users"
                                variant="GreenLight"
                            />
                        ),
                    },
                    {
                        type: 'content',
                        content: (
                            <HeimeText variant="title" style={{ textAlign: 'left', marginBottom: screenMargin }}>
                                {t('sharingAll:helpfulNeighborCategories')}
                            </HeimeText>
                        ),
                    },
                    ...HelpfulNeighborTags.map((category) => ({
                        type: 'category' as const,
                        users: helpfulUsers[category]?.users ?? [],
                        messages: helpfulUsers[category]?.messages ?? [],
                        category,
                    })),
                ]}
                renderItem={({ item }) => {
                    if (item.type === 'content') {
                        return item.content;
                    }
                    return (
                        <HelpfulNeighborCategory
                            {...item}
                            key={item.category}
                            isLoadingUsers={isLoadingMessages}
                            onPress={getHandleNavigate(item.category)}
                            style={{ marginBottom: screenMargin }}
                            isNew={item.messages.some((message) => !isRead(message))}
                            isVisible={visibleItems.has(item.category)}
                        />
                    );
                }}
                estimatedItemSize={100}
            />
        </View>
    );
};

const HelpfulNeighborCategory = ({
    category,
    users,
    isLoadingUsers,
    onPress,
    style,
    isNew,
    isVisible,
}: {
    category: HelpfulNeighborTag;
    users: Array<ExternalUserProfile>;
    isLoadingUsers: boolean;
    onPress: () => void;
    style: StyleProp<ViewStyle>;
    isNew: boolean;
    isVisible: boolean;
}) => {
    const { t } = useTranslation();
    const { theme } = useThemeContext();

    const distinctUsers = useMemo(() => {
        return users.reduce((acc, user) => {
            if (!acc.find((u) => u.id === user.id)) {
                acc.push(user);
            }
            return acc;
        }, [] as Array<ExternalUserProfile>);
    }, [users]);

    const text = useMemo(() => {
        const firstUserWithAvatar = distinctUsers.find((user) => user.avatar) ?? distinctUsers[0];
        if (!firstUserWithAvatar) {
            return null;
        }
        const otherUsers = distinctUsers.filter((user) => user.id !== firstUserWithAvatar.id);
        const otherUsersCount = otherUsers.length;

        return (
            <HeimeText style={{ color: theme.main, flex: 1 }}>
                <HeimeText style={{ fontWeight: 'bold' }}>{firstUserWithAvatar.fname}</HeimeText>
                {otherUsersCount === 0 ? (
                    <HeimeText> {t('sharingAll:wantsToHelp_zero')}</HeimeText>
                ) : (
                    <HeimeText> + {t('sharingAll:wantsToHelp', { count: otherUsersCount })}</HeimeText>
                )}
            </HeimeText>
        );
    }, [distinctUsers, theme.main, t]);

    const displayIsNew = useIsNewDisplayState(isNew, category);

    const imageStyle = {
        width: WW * 0.15,
        height: WW * 0.15,
        borderColor: theme.main,
        borderRadius: 40,
    };

    return (
        <TouchableHeime
            key={category}
            style={[
                {
                    borderWidth: 1,
                    borderColor: theme.secondaryLight,
                    padding: smallestMargin,
                    gap: smallestMargin,
                },
                style,
            ]}
            onPress={onPress}
        >
            <View style={{ gap: smallestMargin, flexDirection: 'row', alignItems: 'center' }}>
                {category === 'other' ? (
                    <Sharing style={imageStyle} />
                ) : (
                    <CacheImage
                        source={Images[category]}
                        style={{
                            width: WW * 0.15,
                            height: WW * 0.15,
                            borderColor: theme.main,
                            borderRadius: 40,
                        }}
                        resizeMode={'cover'}
                    />
                )}
                <View style={{ flex: 1 }}>
                    <HeimeText style={{ fontWeight: 'bold' }}>{t(`sharingAll:${category}_title`)}</HeimeText>
                    <HeimeText style={{ flex: 1, fontSize: undefined }} variant="subtitle">
                        {t(`sharingAll:${category}_description`)}
                    </HeimeText>
                </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: smallestMargin }}>
                <RowUsersDisplay users={distinctUsers} subtext={text} isLoading={isLoadingUsers} />
                <NewTag isNew={displayIsNew} variant="lightGreen" isVisible={isVisible} />
            </View>
        </TouchableHeime>
    );
};

export default HelpfulNeighbor;
